import { IReactiveModel, ViewModel } from '@descomplica/microfrontend-core';
import { differenceInSeconds } from 'date-fns';
import { action, makeObservable, observable } from 'mobx';

import { GetStreamingByGuidCollaboration } from '@/application/collaborations/classroom/get-streaming-by-guid.collaboration';
import { LESSON_TYPE, LiveStatus, TRANSMISSION_TYPE } from '@/application/commons/enums/live.enum';
import {
  ClassifierDto,
  StreamingProviderType,
} from '@/application/interfaces/classroom/pegasus.gateway';
import { MixpanelLessonPlayed } from '@/application/interfaces/classroom/tracking';
import { Tracking } from '@/application/utils/tracking';
import { ClassifiersStore } from '@/presentation/stores/classifiers.store';
import { StudentClassesStore } from '@/presentation/stores/studentClasses.store';

import { ClassroomViewModel, LessonType } from '../classroom/classroom.vm';

type StreamingDto = {
  guid: string;
  title: string;
  description?: string;
  order: number;
  streamingAccountId: string;
  streamingId: string;
  startStreaming: Date;
  endStreaming: Date;
  lessonStatus?: LiveStatus;
};

@ViewModel({ singleton: true })
export class LiveViewModel implements IReactiveModel {
  @observable isLoading = true;
  @observable classifiers: ClassifierDto[] = [];
  @observable highestClassificationLevels: ClassifierDto[] = [];
  @observable livestreamUrl: URL;
  @observable youtubeLiveUrl: URL;
  @observable youtubeChatUrl: URL;
  @observable streamingInfo: StreamingDto;
  @observable streamingProvider: StreamingProviderType;
  @observable showFullscreenChat = false;

  constructor(
    private getStreamingByGuidCollaboration: GetStreamingByGuidCollaboration,
    private classroomViewModel: ClassroomViewModel,
    private classifiersStore: ClassifiersStore,
    private studentClassesStore: StudentClassesStore,
  ) {
    makeObservable(this);
  }

  @action
  async onInit() {
    this.isLoading = false;
    setTimeout(() => {
      this.handleLiveLessonPlayed();
    }, 3000);
  }

  @action
  setClassifiers(classifiers: ClassifierDto[]) {
    this.classifiers = classifiers;
  }

  @action
  async handleScreenEnter(guid: string, streamingGuid: string): Promise<void> {
    this.isLoading = true;
    try {
      const {
        data: { lesson },
      } = await this.getStreamingByGuidCollaboration.run({
        courseGuid: guid,
        streamingGuid,
      });

      if (lesson.lessonType === LessonType.LIVE && lesson?.streaming) {
        const { streamingId, streamingAccountId, streamingProvider } = lesson.streaming;
        this.streamingProvider = streamingProvider;

        if (streamingProvider === StreamingProviderType.LIVESTREAM) {
          const liveUrl = this.buildLivestreamUrl(streamingId, streamingAccountId);

          this.streamingInfo = lesson.streaming;
          this.livestreamUrl = liveUrl;
        }

        if (streamingProvider === StreamingProviderType.YOUTUBE) {
          const liveUrl = this.buildYoutubeLiveUrl(streamingId);
          const chatUrl = this.buildYoutubeChatUrl(streamingId);

          this.streamingInfo = lesson.streaming;
          this.youtubeLiveUrl = liveUrl;
          this.youtubeChatUrl = chatUrl;
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.isLoading = false;
    }
  }

  @action
  handleLiveLessonPlayed() {
    const lessonPlayedProps: MixpanelLessonPlayed = {
      lesson_type: LESSON_TYPE.LIVE,
      type: TRANSMISSION_TYPE.LIVE,
      lesson: this.classroomViewModel.currentLiveLesson?.slug,
      lesson_name: this.classroomViewModel.currentLiveLesson?.name,
      lesson_id: this.classroomViewModel.currentLiveLesson?.guid,
      course: this.classroomViewModel.classroom.course.slug,
      course_name: this.classroomViewModel.classroom.course.name,
      course_id: this.classroomViewModel.classroom.course.guid,
      teacher: this.classroomViewModel.currentLiveLesson?.teacher,
      rewatch: this.classroomViewModel.currentLiveLesson?.isCompleted,
      streamingProvider: this.classroomViewModel?.currentLiveLesson?.streaming?.streamingProvider,
      streamingAccountId: this.classroomViewModel?.currentLiveLesson?.streaming?.streamingAccountId,
      streamingId: this.classroomViewModel?.currentLiveLesson?.streaming?.streamingId,
      ...this.classifiersStore.classifiersTrackingInfo,
      ...this.studentClassesStore.getStudentClassesTrackingInfo(),
    };

    Tracking.lessonPlayed(lessonPlayedProps);
  }

  buildLivestreamUrl = (live: string, livestreamAccountId: string): URL => {
    const url = new URL(
      `${livestreamAccountId}/events/${live}/player`,
      'https://livestream.com/accounts/',
    );
    url.searchParams.set('width', '640');
    url.searchParams.set('height', '360');
    url.searchParams.set('enableInfoAndActivity', 'true');
    url.searchParams.set('defaultDrawer', '');
    url.searchParams.set('autoPlay', 'true');
    url.searchParams.set('mute', 'false');

    return url;
  };

  buildYoutubeLiveUrl = (live: string): URL => {
    `https://www.youtube.com/embed/${`jfKfPfyJRdk`}?autoplay=1&mute=1&hl=pt-br&rel=0`;
    const url = new URL(`${live}`, 'https://www.youtube.com/embed/');
    url.searchParams.set('autoplay', '1');
    url.searchParams.set('mute', '1');
    url.searchParams.set('hl', 'pt-br');
    url.searchParams.set('rel', '0');

    return url;
  };

  buildYoutubeChatUrl = (live: string): URL => {
    const url = new URL('https://www.youtube.com/live_chat');

    url.searchParams.set('v', live);
    url.searchParams.set('embed_domain', window.location.hostname);

    return url;
  };

  getSecondsUntilDate = (dateIsoString: Date): number => {
    const timeNow = new Date();
    const liveStartTime = new Date(dateIsoString);

    return differenceInSeconds(liveStartTime, timeNow);
  };

  setShowFullscreenChat = (showFullscreenChat: boolean) => {
    this.showFullscreenChat = showFullscreenChat;
  };
}
